import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@dopomogai/ui';
import DocumentParagraph from './DocumentParagraph';
import DocumentList from './DocumentList';
import DocumentChecklist from './DocumentChecklist';
import type { DocumentContentItem } from './types/docStructures';

interface RenderContext {
  namespace: string;
  t: ReturnType<typeof useTranslation>['t'];
}

const renderCallout = (content: string, variant: 'info' | 'success' | 'warning' | 'danger' = 'info') => (
  <Alert
    type={
      variant === 'success'
        ? 'success'
        : variant === 'warning'
        ? 'warning'
        : variant === 'danger'
        ? 'error'
        : 'info'
    }
    message={content}
  />
);

const renderCodeBlock = (code: string, language?: string) => (
  <div className="rounded-xl border-2 border-slate-900 bg-slate-900 text-slate-100 shadow-[6px_6px_0px_rgb(15,23,42)]">
    <div className="flex items-center gap-2 bg-slate-800 px-4 py-3 text-xs uppercase tracking-[0.35em] text-slate-400">
      <span>{language ?? 'code'}</span>
    </div>
    <pre className="px-4 py-5 whitespace-pre-wrap text-sm leading-relaxed">
      <code>{code}</code>
    </pre>
  </div>
);

export const renderDocumentContentItem = (
  item: DocumentContentItem,
  index: number,
  namespace: string,
  t: RenderContext['t'],
) => {
  switch (item.type) {
    case 'paragraph': {
      const text = t(item.textKey);
      return <DocumentParagraph key={index} text={text} />;
    }

    case 'list': {
      const values = item.items.map((key) => t(key));
      return <DocumentList key={index} items={values} listType={item.listType} />;
    }

    case 'callout': {
      const content = t(item.textKey);
      return <Fragment key={index}>{renderCallout(content, item.variant)}</Fragment>;
    }

    case 'code': {
      const code = t(item.codeKey);
      return <Fragment key={index}>{renderCodeBlock(code, item.language)}</Fragment>;
    }

    case 'checklist': {
      const resolvedItems = item.items.map((key) => {
        const value = t(key, { returnObjects: true }) as any;
        if (typeof value === 'string') {
          return { id: key, text: value, isComplete: false };
        }
        if (value && typeof value === 'object') {
          return {
            id: value.id ?? key,
            text: value.text ?? '',
            isComplete: Boolean(value.isComplete),
          };
        }
        return { id: key, text: String(value), isComplete: false };
      });

      return (
        <DocumentChecklist
          key={index}
          items={resolvedItems}
          onToggleItem={() => {
            /* no-op for now */
          }}
        />
      );
    }

    case 'component':
    default:
      return null;
  }
};